import {
  cancelDownload,
  writeDownloadedChunksToDisk,
  updateTimeStatusOfDownloadingFileCommon,
  fallbackToZipDownload,
  resumeDownload,
  pausedDownload,
  pauseDownload,
  chunkNosFromList,
  findRemainingChunks,
  getPercentage,
} from "/WebInterface/new-ui/modules/advanced-download/common.js";
import downloadingQueue from "/WebInterface/new-ui/modules/advanced-download/downloading-queue.js";
import store from "/WebInterface/new-ui/store/index.js";
import C from "/WebInterface/new-ui/assets/js/utils/log.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import {
  isFileOpen,
  closeFile,
  chunkAckXHR,
} from "/WebInterface/new-ui/upload/utils/apis.js";
import { ajax } from "/WebInterface/new-ui/assets/js/utils/ajax.js";
import { growl } from "/WebInterface/new-ui/modules/toast.js";

/**
 * File Downloading Activities Module
 * @constructor
 * @param {array} queue - Maintains the queue of files to download
 * @param {integer} maxRequestPerDomain - Max API requests we can hit simultaneously while downloading file
 * @param {object} streamingFile - Current downloading file
 * @param {integer} chunkWrittenToTheDisk - Number of the chunk written in the local disk
 * @param {integer} bytesDownloaded - Total bytes downloaded
 * @param {integer} bytesWritten - Total bytes written in the local disk
 * @param {object} chunksRemainsToWrite - Maintains the unordered downloaded chunks that are waiting to write in the local disk
 * @param {array} chunks - Maintains chunks of the current downloading file
 * @param {integer} elapsedTime - Elapsed time of currently downloading file in seconds
 * @param {integer} lastBytesDownloaded - Number of bytes downloaded in the last second (For speed calculation).
 * @param {boolean} isPause - Keep status of pause of the current downloading file.
 * @param {integer} maxRetries - Max retries allows to the current downloading file before transferring to failed status.
 * @param {boolean} fileDownloadingInProgress - Keep status of downloading process.
 */

class WSAdvanceDownload {
  constructor({
    maxRequest = 10,
    showServerReadProgress = false,
    testRecovery = false,
  }) {
    this.showServerReadProgress = showServerReadProgress;
    this.testRecovery = testRecovery;
    C.log("WS Download initialized with:", {
      maxRequest,
      showServerReadProgress,
      testRecovery,
    });
    this.streamingFile = null;
    this.lastWrittenChunkIndex = 0;
    this.chunkWrittenToTheDisk = 1;
    this.bytesDownloaded = 0;
    this.bytesWritten = 0;
    this.bytesReceivedForSpeed = 0;
    this.lastBytesReceivedForSpeed = 0;
    this.chunks = [];
    this.chunksDownloaded = {};
    this.chunksRemainsToWrite = {};
    this.chunksBeingWritten = {};
    this.chunksWritten = {};
    this.elapsedTime = 0;
    this.lastBytesDownloaded = 0;
    this.lastBytesDownloadedInTenSec = 0;
    this.isPause = false;
    this.maxRetries = 2;
    this.maxRequestPerDomain = 3;
    this.maxRequest = maxRequest;
    this.retries = 0;
    this.retriedChunks = [];
    this.lastIndexOfChunkWrittenToDisk = 0;
    this.lastMessageTime = null;
    this.fileDownloaded = false;
    this.lastReceivedChunkIndex = 0;
    this.EOFChunkIndex = null;
    this.lastUpdateTime = null;
    this.lastRandomCloseTime = Date.now();
    this.randomCloseInterval = 10000;
    this.missingChunksTracker = {}; // Track missing chunks and when they were first detected
    this.missingChunkRecoveryTimeout = 90000; // 90 seconds before triggering recovery
    this.missingChunkCheckInterval = null;
    this.lastMissingChunkCheck = null;
    this.recoveryInProgress = false;
    this.diskWriteErrorHandled = false;
    this.socketSequence = 0;
    this.recoveryWaitStart = null;
    this.maxRecoveryWait = 90000;
    this.maxRecoveryAttempts = 3;
    this.recoveryAttemptCount = 0;
    this.recoveryPendingChunks = [];
    this.pendingAckChunks = new Set();
    this.ackFlushIntervalId = null;
    this.ackFlushIntervalMs = 1000;
    this.isAckFlushInProgress = false;
    this.maxAckBatchSize = 50;
    this.isFileOpenCheckInProgress = false;
    this.maxIsOpenRetries = 10;
    this.isOpenRetryDelay = 500;
    this.lastAckBelowSentAt = 0;
    this.ackBelowIntervalMs = 10000;
    this.isAckBelowInProgress = false;
    this.lastAckBelowChunkIndex = null;
    this.sessionExpired = false;
  }

  // Reset required vars on new file download
  resetVars = () => {
    this.sockets = [];
    this.chunkNum = 0;
    this.canceled = false;
    this.fileClosed = false;
    this.isPause = false;
    this.compressingTimeoutId = null;
    this.pendingChunks = [];
    this.lastIndexOfChunkWrittenToDisk = 0;
    this.lastWrittenChunkIndex = 0;
    this.fileDownloaded = false;
    this.chunksDownloaded = {};
    this.chunksRemainsToWrite = {};
    this.chunksBeingWritten = {};
    this.chunksWritten = {};
    this.lastReceivedChunkIndex = 0;
    this.EOFChunkIndex = null;
    this.missingChunksTracker = {};
    this.recoveryInProgress = false;
    this.diskWriteErrorHandled = false;
    this.socketSequence = 0;
    this.recoveryWaitStart = null;
    this.recoveryAttemptCount = 0;
    this.recoveryPendingChunks = [];
    this.pendingAckChunks = new Set();
    this.isAckFlushInProgress = false;
    this.lastAckBelowSentAt = 0;
    this.isAckBelowInProgress = false;
    this.lastAckBelowChunkIndex = null;
    this.sessionExpired = false;
    this.downloadStartTime = null;
    this.downloadEndTime = null;
    if (this.ackFlushIntervalId) {
      clearInterval(this.ackFlushIntervalId);
      this.ackFlushIntervalId = null;
    }

    // Keep only the last file's debug info to avoid memory issues
    this.downloadSummary = {
      chunkEvents: [],
    };

    // Clear debug logs before starting each new download to avoid mixing data between files
    // This ensures we only keep the last file's debug info to avoid memory issues
    if (typeof C !== "undefined" && C.clearDebugLogs) {
      C.clearDebugLogs();
    }

    store.dispatch("stopAliveSession");
  };

  logNoMessage = () => {
    if (this.lastMessageTime === null) {
      return;
    }

    const timeElapsed = Date.now() - this.lastMessageTime;
    if (timeElapsed > 10000) {
      C.error(
        `No message received from server for ${timeElapsed / 1000} seconds`
      );

      const chunksWrtigin = Object.keys(this.chunksBeingWritten).filter(
        (key) =>
          !Object.prototype.hasOwnProperty.call(this.chunksBeingWritten, key)
      );
      const chunksYetToWrite = Object.keys(this.chunksRemainsToWrite).filter(
        (key) =>
          !Object.prototype.hasOwnProperty.call(this.chunksRemainsToWrite, key)
      );
      C.warn(
        `Chunks being written: ${chunksWrtigin.length} - ${chunksWrtigin}. Chunk yet to write: ${chunksYetToWrite.length} - ${chunksYetToWrite}. Last received chunk: ${this.lastReceivedChunkIndex}. Last written chunk: ${this.lastWrittenChunkIndex}.`
      );
      this.lastMessageTime = Date.now();
    }
  };

  removeSocketFromStorage = (socket) => {
    if (!socket) return;
    if (socket.readyState === WebSocket.OPEN) {
      socket.close();
      return;
    }
    const socketId = socket.debugId || "unknown";
    if (socket && socket.removeEventListener) {
      const socketEvents = Object.getOwnPropertyNames(socket);
      for (const event of socketEvents) {
        if (event.startsWith("on") && typeof socket[event] === "function") {
          socket.removeEventListener(event.slice(2), socket[event]);
        }
      }
    }
    const index = this.sockets.indexOf(socket);
    if (index !== -1) {
      this.sockets.splice(index, 1);
      C.log(`Download: Socket#${socketId} removed from tracking`);
    }
  };

  // create new socket for downloading file :- runs when file starts downloading
  createSocket = (reason = "initial") => {
    const { size: fileSize, id: fileId } = this.streamingFile;
    const transferId = this.transferId;
    const { hostname, port, protocol } = location;
    const ws_protocol = "ws" + (protocol.startsWith("https") ? "s" : "");
    const sockets = this.sockets;
    if (this.fileClosed) {
      C.warn(
        `Download: Skipping socket creation (reason: ${reason}) because file is already closed`
      );
      return;
    }
    const socketId = ++this.socketSequence;
    const socket = new WebSocket(
      `${ws_protocol}:/${hostname}:${port}/DOWNLOAD/${transferId}`
    );
    socket.binaryType = "arraybuffer";
    socket.chunksBeingSent = []; // Initialize chunksBeingSent array
    socket.index = sockets.length; // Add socket index
    socket.debugId = socketId;
    socket.creationReason = reason;
    socket.createdAt = Date.now();

    sockets.push(socket);
    this.setSocketMessageHandler(socket);
    C.log(
      `Download: Socket#${socketId} connection requested (reason: ${reason}, target: ${hostname}:${port})`
    );
    // socket on open
    socket.onopen = () => {
      const openDelay = Date.now() - socket.createdAt;
      C.log(
        `Download: Socket#${socket.debugId} opened in ${openDelay}ms (reason: ${socket.creationReason})`
      );
      this.lastMessageTime = Date.now();
    };

    // socket on close
    socket.onclose = (event) => {
      const duration = Date.now() - socket.createdAt;
      C.warn(
        `Download: Socket#${socket.debugId} closed (code: ${
          event.code
        }, clean: ${event.wasClean}, reason: ${
          event.reason || "n/a"
        }, lifetime: ${duration}ms)`
      );
      this.removeSocketFromStorage(socket);
      this.handleSocketConnectionError(socket);
    };

    // socket on error
    socket.onerror = (event) => {
      C.warn(
        `Download: Socket#${socket.debugId} error: ${event?.message || "n/a"}`
      );
      this.removeSocketFromStorage(socket);
      this.handleSocketConnectionError(socket);
    };

    // socket onmessage handler is configured via setSocketMessageHandler(socket)
  };

  checkIfFileDownloaded = () => {
    const { id: fileId } = this.streamingFile;
    const allChunks = chunkNosFromList(this.chunksDownloaded);
    const writtenChunks = chunkNosFromList(this.chunksWritten);
    const missingChunks = findRemainingChunks(allChunks);

    C.log(
      `Download: All Chunks length: ${allChunks.length}, Written Chunks length: ${writtenChunks.length}, Missing Chunks length: ${missingChunks.length}, EOFChunkIndex: ${this.EOFChunkIndex}`
    );

    // Track missing chunks for recovery
    this.updateMissingChunksTracker(missingChunks);

    if (this.EOFChunkIndex !== null && missingChunks.length > 0) {
      C.warn(
        `Download: Received last chunk, but missing chunks: ${missingChunks.join(
          ", "
        )}. So waiting for missing chunks. Chunks length ${
          allChunks.length
        }, written chunks: ${writtenChunks.length}, missing chunks: ${
          missingChunks.length
        }`
      );
    }
    this.fileDownloaded =
      this.EOFChunkIndex !== null &&
      missingChunks.length === 0 &&
      allChunks.length - 1 === writtenChunks.length &&
      allChunks.length >= this.EOFChunkIndex;
    if (this.fileDownloaded) {
      C.success(
        `Download: Completed, writing to disk may take time, once all chunks are written it will start finalization`
      );
      if (this.compressingTimeoutId) return;

      store.dispatch("keepSessionAlive");

      this.compressingTimeoutId = setTimeout(() => {
        store.dispatch("updateFileStatusDownload", {
          fileId,
          updates: {
            status: "compressing",
          },
        });
      }, 3000);
      this.fileClosed = true;
      this.closeAllSockets();
      this.stopAckFlushInterval({ flush: true });
    }
  };

  // Track missing chunks and when they were first detected
  updateMissingChunksTracker = (missingChunks) => {
    const now = Date.now();
    const currentMissingSet = new Set(missingChunks);

    // Remove chunks that are no longer missing
    for (const chunkIndex in this.missingChunksTracker) {
      if (!currentMissingSet.has(parseInt(chunkIndex))) {
        delete this.missingChunksTracker[chunkIndex];
        C.log(
          `Download: Chunk ${chunkIndex} no longer missing, removed from tracker`
        );
      }
    }

    // Add newly detected missing chunks
    missingChunks.forEach((chunkIndex) => {
      if (!this.missingChunksTracker[chunkIndex]) {
        this.missingChunksTracker[chunkIndex] = {
          firstDetected: now,
          lastChecked: now,
        };
        C.warn(
          `Download: Missing chunk ${chunkIndex} detected and tracked for recovery`
        );
      } else {
        // Update last checked time
        this.missingChunksTracker[chunkIndex].lastChecked = now;
      }
    });
  };

  // Check if missing chunks need recovery
  checkMissingChunksRecovery = () => {
    // Multiple safety checks to prevent interference with normal operations
    if (
      this.fileClosed ||
      this.isPause ||
      this.fileDownloaded ||
      !this.streamingFile ||
      !this.transferId
    ) {
      return;
    }

    const now = Date.now();

    if (this.recoveryInProgress) {
      C.store("Download: Recovery already running, skipping new trigger");
      return;
    }
    const missingChunksNeedingRecovery = [];
    const allChunks = chunkNosFromList(this.chunksDownloaded);
    const missingChunks = findRemainingChunks(allChunks);

    // Update tracker with current missing chunks
    this.updateMissingChunksTracker(missingChunks);

    // Only trigger recovery if chunks have been missing for the full timeout period
    // This ensures we don't interfere with normal out-of-order chunk delivery
    for (const chunkIndex in this.missingChunksTracker) {
      const tracker = this.missingChunksTracker[chunkIndex];
      const timeSinceFirstDetected = now - tracker.firstDetected;

      if (timeSinceFirstDetected >= this.missingChunkRecoveryTimeout) {
        missingChunksNeedingRecovery.push(parseInt(chunkIndex));
      }
    }

    // Only trigger recovery if we have chunks that have been missing for the full timeout
    // This is conservative and safe - won't interfere with normal operations
    if (missingChunksNeedingRecovery.length > 0) {
      C.warn(
        `Download: Missing chunks detected for ${
          this.missingChunkRecoveryTimeout / 1000
        }s: ${missingChunksNeedingRecovery.join(", ")}. Triggering recovery.`
      );
      this.triggerMissingChunkRecovery(missingChunksNeedingRecovery);
    }
  };

  // Trigger recovery for missing chunks by closing all sockets and reconnecting
  triggerMissingChunkRecovery = async (missingChunks) => {
    // Additional safety checks before recovery
    if (this.recoveryInProgress) {
      C.warn("Download: Recovery already in progress, skipping");
      return;
    }

    // Double-check state hasn't changed
    if (
      this.fileClosed ||
      this.isPause ||
      this.fileDownloaded ||
      !this.streamingFile
    ) {
      C.warn("Download: State changed, aborting recovery");
      return;
    }

    if (this.recoveryAttemptCount >= this.maxRecoveryAttempts) {
      this.failRecoveryAttempts();
      return;
    }
    this.recoveryAttemptCount++;

    this.recoveryInProgress = true;
    this.recoveryWaitStart = Date.now();
    this.recoveryPendingChunks = [...missingChunks];
    this.lastMessageTime = Date.now();
    C.warn(
      `Download: Starting recovery for missing chunks: ${missingChunks.join(
        ", "
      )}`
    );

    try {
      // Close all existing sockets to force reconnection
      // This uses the same pattern as existing socket error handling
      const openSockets = this.sockets.filter(
        (socket) => socket.readyState === WebSocket.OPEN
      );

      if (openSockets.length > 0) {
        C.warn(
          `Download: Closing ${openSockets.length} sockets to trigger recovery`
        );
        openSockets.forEach((socket) => {
          C.warn(
            `Download: Closing socket#${
              socket.debugId
            } to trigger recovery (reason: missing chunks ${missingChunks.join(
              ", "
            )})`
          );
          socket.close();
        });
      }

      // Wait a bit before recreating sockets to ensure old connections are closed
      await new Promise((resolve) => setTimeout(resolve, 1000));

      // Recreate all sockets and wait for them to open
      if (!this.fileClosed && !this.isPause && this.streamingFile) {
        C.log("Download: Recreating all sockets after recovery trigger");
        try {
          await this.connectAllSockets();
          // At least 50% of sockets are now open, mark recovery as complete
          const openCount = this.sockets.filter(
            (s) => s.readyState === WebSocket.OPEN
          ).length;
          this.markRecoveryProgress(
            `${openCount}/${this.maxRequest} sockets opened (partial success)`
          );
          C.log(
            `Download: Recovery completed - ${openCount}/${this.maxRequest} sockets are ready`
          );
        } catch (error) {
          C.error(
            `Download: Failed to open all sockets during recovery: ${error.message}`
          );
          // If we couldn't open all sockets, check if we should retry or fail
          if (this.recoveryAttemptCount >= this.maxRecoveryAttempts) {
            this.failRecoveryAttempts();
            return;
          }
          // Release recovery lock so we can retry
          this.recoveryInProgress = false;
          this.recoveryWaitStart = null;
        }
      }

      // Clear the missing chunks tracker for the chunks we're recovering
      // They'll be re-added if still missing after recovery
      missingChunks.forEach((chunkIndex) => {
        delete this.missingChunksTracker[chunkIndex];
      });
    } catch (error) {
      C.error(`Download: Error during recovery: ${error.message}`);
      this.recoveryInProgress = false;
      this.recoveryWaitStart = null;
    }
  };

  // handles socket connection error
  handleSocketConnectionError = async (socket) => {
    if (socket && socket.readyState === WebSocket.OPEN) return;
    if (this.fileClosed || this.isPause) return;
    if (this.isFileOpenCheckInProgress) {
      C.warn("Download: isOpen check already in progress, skipping.");
      return;
    }
    this.isFileOpenCheckInProgress = true;
    let response;
    try {
      const checkResult = await this.checkFileOpenStatusWithRetry();
      if (!checkResult.success) {
        C.warn(
          `Download: Unable to verify file status after ${this.maxIsOpenRetries} attempts, assuming download still active`
        );
        return;
      }
      response = checkResult.response;
    } finally {
      this.isFileOpenCheckInProgress = false;
    }
    if (
      response === "CLOSED" ||
      response?.toLowerCase().indexOf("failure") >= 0 ||
      response?.toLowerCase().indexOf("error") >= 0
    ) {
      if (this.fileClosed || this.isPause) {
        C.error("Download: File is downloaded or canceled, ignoreing isOpen");
        return;
      }
      this.closeFileWithError("failed");
      C.error("Download: isOpen for download failed, canceling download");
      return;
    }
    if (!this.fileClosed && !this.isPause) {
      this.recreateSocketOnError();
    }
  };

  checkFileOpenStatusWithRetry = async () => {
    for (let attempt = 1; attempt <= this.maxIsOpenRetries; attempt++) {
      try {
        C.log(
          `Download: isFileOpen call (attempt ${attempt}/${this.maxIsOpenRetries}) - transfer_id: ${this.transferId}`
        );
        const response = await isFileOpen({
          transfer_type: "download",
          download_id: this.transferId,
          random: Math.random(),
        });
        C.log(
          `Download: isFileOpen response (attempt ${attempt}): ${response}`
        );
        return { success: true, response };
      } catch (error) {
        C.warn(
          `Download: isOpen check failed (attempt ${attempt}/${
            this.maxIsOpenRetries
          }): ${error?.message || error}`
        );
        if (attempt === this.maxIsOpenRetries) {
          return { success: false, error };
        }
        await this.delay(this.isOpenRetryDelay * attempt);
      }
    }
    return { success: false };
  };

  startDownloading = () => {
    if (this.fileClosed) return;
    clearInterval(this.noMsgIntervalId);
    this.noMsgIntervalId = setInterval(this.logNoMessage, 5000);

    // Start missing chunk monitoring
    this.startMissingChunkMonitoring();
    this.startAckFlushInterval();

    const openSockets = this.sockets.filter(
      (socket) =>
        socket.readyState === WebSocket.OPEN ||
        socket.readyState === WebSocket.CONNECTING
    );
    const socketsToAdd = Math.max(0, this.maxRequest - openSockets.length);
    if (socketsToAdd > 0) {
      for (let x = 0; x < socketsToAdd; x++) {
        this.createSocket("initial");
      }
    }
    downloadingQueue.handleUpdateDownloadingStatus(true);
    this.updateTimeStatusOfDownloadingFile();

    if (this.testRecovery) {
      this.randomlyCloseSocket();
    }
  };

  startAckFlushInterval = () => {
    if (this.ackFlushIntervalId) {
      return;
    }
    this.ackFlushIntervalId = setInterval(() => {
      this.flushPendingAcks();
    }, this.ackFlushIntervalMs);
  };

  stopAckFlushInterval = ({ flush = false } = {}) => {
    if (this.ackFlushIntervalId) {
      clearInterval(this.ackFlushIntervalId);
      this.ackFlushIntervalId = null;
    }
    if (flush) {
      this.flushPendingAcks();
    }
  };

  queueAckChunk = (chunkIndex) => {
    if (this.sessionExpired || this.fileClosed) {
      return;
    }
    if (chunkIndex === undefined || chunkIndex === null) {
      return;
    }
    if (!this.pendingAckChunks) {
      this.pendingAckChunks = new Set();
    }
    this.pendingAckChunks.add(chunkIndex);
    this.logChunkEvent(chunkIndex, "CHUNK_ACK_QUEUED");
    if (
      this.pendingAckChunks.size >= this.maxAckBatchSize &&
      !this.isAckFlushInProgress
    ) {
      this.flushPendingAcks();
    }
  };

  flushPendingAcks = async () => {
    if (this.sessionExpired || this.fileClosed) {
      return;
    }

    const shouldSkipMainAck =
      this.isAckFlushInProgress ||
      !this.pendingAckChunks ||
      this.pendingAckChunks.size === 0 ||
      !this.transferId;
    if (shouldSkipMainAck) {
      await this.sendAckBelowIfDue();
      return;
    }

    const chunks = Array.from(this.pendingAckChunks);
    this.pendingAckChunks.clear();
    this.isAckFlushInProgress = true;
    const chunkList = chunks.sort((a, b) => a - b).join(",");

    try {
      C.log(
        `Download: chunkAckXHR call - transfer_id: ${this.transferId}, chunks: ${chunkList}`
      );
      const response = await chunkAckXHR({
        transfer_type: "download",
        download_id: this.transferId,
        chunk_num: chunkList,
      });
      C.log(
        `Download: chunkAckXHR response - ACKed ${chunks.length} chunk(s) (chunks: ${chunkList}), response: ${response}`
      );
      this.logChunkEvent(null, "CHUNK_ACK_FLUSH", { chunks: chunkList });
    } catch (error) {
      const errorMessage = error?.message || String(error);
      C.warn(
        `Download: chunkAckXHR failed (chunks: ${chunkList}): ${errorMessage}`
      );
      this.logChunkEvent(null, "CHUNK_ACK_FLUSH_FAIL", {
        chunks: chunkList,
        error: errorMessage,
      });

      if (
        errorMessage.includes("Not logged in") ||
        errorMessage.includes("session expired") ||
        errorMessage.includes("authentication")
      ) {
        if (!this.sessionExpired) {
          this.sessionExpired = true;
          const sessionMsg =
            "Session expired. Please log in again and retry the download.";
          C.error(
            "Download: Session expired detected (Not logged in). Canceling download and closing all resources."
          );
          await this.closeFileWithError(sessionMsg);
        }
        return;
      }

      chunks.forEach((chunk) => this.pendingAckChunks.add(chunk));
    } finally {
      this.isAckFlushInProgress = false;
      await this.sendAckBelowIfDue();
    }
  };

  sendAckBelowIfDue = async () => {
    if (this.sessionExpired || this.fileClosed) {
      return;
    }

    if (
      this.isAckBelowInProgress ||
      !this.transferId ||
      !this.chunksWritten ||
      Object.keys(this.chunksWritten).length === 0
    ) {
      return;
    }

    const now = Date.now();
    if (
      this.lastAckBelowSentAt &&
      now - this.lastAckBelowSentAt < this.ackBelowIntervalMs
    ) {
      return;
    }

    const chunkIndex = this.lastWrittenChunkIndex;

    // Skip if the chunk index hasn't changed since last call
    if (this.lastAckBelowChunkIndex === chunkIndex) {
      const totalWrittenChunks = Object.keys(this.chunksWritten).length;
      const bytesWrittenMB = (this.bytesWritten / (1024 * 1024)).toFixed(2);
      C.log(
        `Download: Skipping ack_below - chunk index ${chunkIndex} unchanged since last call, lastWrittenChunkIndex: ${this.lastWrittenChunkIndex}, totalWrittenChunks: ${totalWrittenChunks}, bytesWritten: ${this.bytesWritten} (${bytesWrittenMB} MB)`
      );
      return;
    }

    this.isAckBelowInProgress = true;
    try {
      const totalWrittenChunks = Object.keys(this.chunksWritten).length;
      const bytesWrittenMB = (this.bytesWritten / (1024 * 1024)).toFixed(2);
      C.log(
        `Download: chunkAckXHR call (ack_below) - transfer_id: ${this.transferId}, chunk_num: ${chunkIndex}, ack_below: true, lastWrittenChunkIndex: ${this.lastWrittenChunkIndex}, totalWrittenChunks: ${totalWrittenChunks}, bytesWritten: ${this.bytesWritten} (${bytesWrittenMB} MB)`
      );
      const response = await chunkAckXHR({
        transfer_type: "download",
        download_id: this.transferId,
        chunk_num: chunkIndex,
        ack_below: true,
      });
      this.lastAckBelowSentAt = now;
      this.lastAckBelowChunkIndex = chunkIndex;
      C.log(
        `Download: chunkAckXHR response (ack_below) - ACKed chunk ${chunkIndex} with ack_below=true (master), lastWrittenChunkIndex: ${this.lastWrittenChunkIndex}, totalWrittenChunks: ${totalWrittenChunks}, bytesWritten: ${this.bytesWritten} (${bytesWrittenMB} MB), response: ${response}`
      );
      this.logChunkEvent(chunkIndex, "CHUNK_ACK_BELOW");
    } catch (error) {
      const errorMessage = error?.message || String(error);
      const totalWrittenChunks = Object.keys(this.chunksWritten).length;
      const bytesWrittenMB = (this.bytesWritten / (1024 * 1024)).toFixed(2);
      C.warn(
        `Download: chunkAckXHR failed (ack_below, chunk ${chunkIndex}) - lastWrittenChunkIndex: ${this.lastWrittenChunkIndex}, totalWrittenChunks: ${totalWrittenChunks}, bytesWritten: ${this.bytesWritten} (${bytesWrittenMB} MB), error: ${errorMessage}`
      );
      this.logChunkEvent(chunkIndex, "CHUNK_ACK_BELOW_FAIL", {
        error: errorMessage,
      });

      if (
        errorMessage.includes("Not logged in") ||
        errorMessage.includes("session expired") ||
        errorMessage.includes("authentication")
      ) {
        if (!this.sessionExpired) {
          this.sessionExpired = true;
          const sessionMsg =
            "Session expired. Please log in again and retry the download.";
          C.error(
            "Download: Session expired detected (Not logged in). Canceling download and closing all resources."
          );
          await this.closeFileWithError(sessionMsg);
        }
      }
    } finally {
      this.isAckBelowInProgress = false;
    }
  };

  // Start periodic monitoring for missing chunks
  startMissingChunkMonitoring = () => {
    if (this.missingChunkCheckInterval) {
      clearInterval(this.missingChunkCheckInterval);
    }

    // Check for missing chunks every 5 seconds
    this.missingChunkCheckInterval = setInterval(() => {
      if (this.fileClosed || this.isPause || this.fileDownloaded) {
        clearInterval(this.missingChunkCheckInterval);
        this.missingChunkCheckInterval = null;
        return;
      }
      this.checkMissingChunksRecovery();
    }, 5000);
  };

  recreateSocketOnError = () => {
    if (this.fileClosed || this.isPause) return;
    const openSockets = this.sockets.filter(
      (socket) =>
        socket.readyState === WebSocket.OPEN ||
        socket.readyState === WebSocket.CONNECTING
    );
    const openCount = openSockets.length;
    const socketsToAdd = Math.max(0, this.maxRequest - openCount);
    C.log(
      `Download: Recreating sockets (open: ${openCount}/${this.maxRequest}, need: ${socketsToAdd})`
    );
    for (let x = 0; x < socketsToAdd; x++) {
      this.createSocket("recovery");
    }
  };

  // Connect all required sockets and wait for them to be ready (used during recovery)
  connectAllSockets = async () => {
    if (this.fileClosed || this.isPause) {
      return Promise.reject(new Error("File closed or paused"));
    }

    const openSockets = this.sockets.filter(
      (socket) =>
        socket.readyState === WebSocket.OPEN ||
        socket.readyState === WebSocket.CONNECTING
    );
    const openCount = openSockets.length;
    const socketsToAdd = Math.max(0, this.maxRequest - openCount);

    if (socketsToAdd === 0) {
      C.log(`Download: All ${openCount} sockets already open`);
      return Promise.resolve();
    }

    C.log(
      `Download: Creating ${socketsToAdd} sockets and waiting for all to open (currently ${openCount}/${this.maxRequest})`
    );

    const socketPromises = [];
    const createdSockets = [];

    // Create all sockets and track their open promises
    for (let x = 0; x < socketsToAdd; x++) {
      const socketPromise = new Promise((resolve, reject) => {
        const socketId = ++this.socketSequence;
        const { hostname, port, protocol } = location;
        const ws_protocol = "ws" + (protocol.startsWith("https") ? "s" : "");
        const socket = new WebSocket(
          `${ws_protocol}:/${hostname}:${port}/DOWNLOAD/${this.transferId}`
        );
        socket.binaryType = "arraybuffer";
        socket.chunksBeingSent = [];
        socket.index = this.sockets.length;
        socket.debugId = socketId;
        socket.creationReason = "recovery";
        socket.createdAt = Date.now();
        socket._openResolver = resolve;
        socket._openRejecter = reject;

        this.sockets.push(socket);
        createdSockets.push(socket);
        this.setSocketMessageHandler(socket);

        C.log(
          `Download: Socket#${socketId} connection requested (reason: recovery, target: ${hostname}:${port})`
        );

        const timeoutId = setTimeout(() => {
          reject(
            new Error(
              `Socket#${socketId} failed to open within ${
                this.maxRecoveryWait / 1000
              }s`
            )
          );
        }, this.maxRecoveryWait);

        // Set up error and close handlers before opening
        socket.onerror = (event) => {
          clearTimeout(timeoutId);
          reject(
            new Error(
              `Socket#${socketId} connection error: ${event?.message || "n/a"}`
            )
          );
        };

        socket.onclose = (event) => {
          clearTimeout(timeoutId);
          if (event.code !== 1000 && socket.readyState !== WebSocket.OPEN) {
            reject(
              new Error(
                `Socket#${socketId} closed unexpectedly (code: ${event.code})`
              )
            );
          }
        };

        socket.onopen = () => {
          clearTimeout(timeoutId);
          const openDelay = Date.now() - socket.createdAt;
          C.log(
            `Download: Socket#${socket.debugId} opened in ${openDelay}ms (reason: ${socket.creationReason})`
          );
          this.lastMessageTime = Date.now();

          // Set up message handler
          socket.onmessage = (event) => {
            return this.handleIncomingSocketMessage(socket, event);
          };

          // Update handlers for post-open operation (these will be called after socket is open)
          const originalOnClose = socket.onclose;
          socket.onclose = (event) => {
            clearTimeout(timeoutId);
            const duration = Date.now() - socket.createdAt;
            C.warn(
              `Download: Socket#${socket.debugId} closed (code: ${
                event.code
              }, clean: ${event.wasClean}, reason: ${
                event.reason || "n/a"
              }, lifetime: ${duration}ms)`
            );
            this.removeSocketFromStorage(socket);
            this.handleSocketConnectionError(socket);
          };

          const originalOnError = socket.onerror;
          socket.onerror = (event) => {
            clearTimeout(timeoutId);
            C.warn(
              `Download: Socket#${socket.debugId} error: ${
                event?.message || "n/a"
              }`
            );
            this.removeSocketFromStorage(socket);
            this.handleSocketConnectionError(socket);
          };

          resolve(socket);
        };

        socket.onerror = (event) => {
          clearTimeout(timeoutId);
          reject(
            new Error(
              `Socket#${socketId} connection error: ${event?.message || "n/a"}`
            )
          );
        };

        socket.onclose = (event) => {
          clearTimeout(timeoutId);
          if (event.code !== 1000) {
            reject(
              new Error(
                `Socket#${socketId} closed unexpectedly (code: ${event.code})`
              )
            );
          }
        };
      });

      socketPromises.push(socketPromise);
    }

    try {
      const results = await Promise.allSettled(socketPromises);
      const successful = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.filter((r) => r.status === "rejected").length;
      const minRequired = socketsToAdd > 6 ? Math.ceil(socketsToAdd * 0.5) : 1; // 50% minimum if > 6, else at least 1

      C.log(
        `Download: Socket connection results: ${successful} succeeded, ${failed} failed (required: ${minRequired})`
      );

      if (successful < minRequired) {
        // Less than 50% opened - treat as failure
        const errorMsg = `Only ${successful}/${socketsToAdd} sockets opened (minimum ${minRequired} required)`;
        C.error(`Download: ${errorMsg}`);
        // Clean up any sockets that didn't open
        createdSockets.forEach((socket) => {
          if (socket.readyState !== WebSocket.OPEN) {
            socket.close();
            this.removeSocketFromStorage(socket);
          }
        });
        throw new Error(errorMsg);
      }

      // At least 50% opened - success, but try to reopen failed ones
      if (failed > 0) {
        C.warn(
          `Download: ${successful}/${socketsToAdd} sockets opened successfully. Attempting to reopen ${failed} failed sockets...`
        );
        // Try to reopen failed sockets (non-blocking)
        const failedSockets = createdSockets.filter(
          (socket) => socket.readyState !== WebSocket.OPEN
        );
        failedSockets.forEach((socket) => {
          this.removeSocketFromStorage(socket);
        });
        // Create replacement sockets for the failed ones
        for (let x = 0; x < failed; x++) {
          this.createSocket("recovery-retry");
        }
      } else {
        C.log(
          `Download: All ${socketsToAdd} sockets opened successfully for recovery`
        );
      }
    } catch (error) {
      C.error(
        `Download: Failed to open minimum required sockets during recovery: ${error.message}`
      );
      // Clean up any sockets that didn't open
      createdSockets.forEach((socket) => {
        if (socket.readyState !== WebSocket.OPEN) {
          socket.close();
          this.removeSocketFromStorage(socket);
        }
      });
      throw error;
    }
  };

  // Check next file in the queue and will start downloading the next file
  nextFileInQueue(canceled) {
    downloadingQueue.nextFileInDownloadingQueue(canceled);
    // nextFileInDownloadingQueue(this, canceled);
  }

  // Calculate speed and time remaining to download the file
  updateTimeStatusOfDownloadingFile = () => {
    updateTimeStatusOfDownloadingFileCommon(this);
  };

  // Initiate downloading of the file
  startFileDownloading = async (file) => {
    const initDownload = async () => {
      this.resetVars();

      // Reset all required variables
      this.streamingFile = file;
      this.chunkWrittenToTheDisk = 1;
      this.bytesDownloaded = 0;
      this.bytesWritten = 0;
      this.bytesReceivedForSpeed = 0;
      this.lastBytesReceivedForSpeed = 0;
      this.chunksDownloaded = {};
      this.chunksRemainsToWrite = {};
      this.chunksBeingWritten = {};
      this.chunksWritten = {};
      this.chunks = [];
      this.elapsedTime = 0;
      this.lastBytesDownloaded = 0;
      this.lastBytesDownloadedInTenSec = 0;
      this.isPause = false;
      this.retrying = false;
      this.retries = 0;
      this.lastChunkIndex = null;
      this.EOFChunkIndex = null;
      this.downloadStartTime = Date.now();
      this.downloadEndTime = null;

      const { id: fileId } = file;

      const c2f = utils.cookie.get("currentAuth");

      // Provides unique id to download the file
      try {
        const resp = await ajax.call({
          timeout: 10000000,
          data: {
            command: `download`,
            transfer_type: "download",
            path: file.href_path,
            c2f,
          },
        });

        if (resp) {
          const xml = new DOMParser().parseFromString(resp, "text/xml");
          const result = utils.xmlToJson(xml);

          this.transferId = result?.commandResult?.response;

          if (this.fileClosed) {
            return;
          }

          this.writableStream = await file.fileHandle.createWritable();

          store.dispatch("updateFileStatusDownload", {
            fileId,
            updates: {
              status: "downloading",
            },
          });

          C.success(
            `Download: File download started. Transfer ID: ${this.transferId}, File ID: ${fileId}, Path: ${file.href_path}`
          );

          // Start downloading chunks
          this.startDownloading();
        }
      } catch (error) {
        if (this.fileClosed) {
          return;
        }
        C.error("Download: WS initDownload/createWritable catch debug", {
          fileId: file?.id,
          transferId: this.transferId,
          storageMethod: file?.storageMethod,
          errorName: error?.name,
          errorMessage: error?.message,
          errorCauseName: error?.cause?.name,
          errorCauseMessage: error?.cause?.message,
        });

        this.cancel();
      } finally {
        if (this.fileClosed) {
          C.log(
            `Download: closeFile call (start download error) - transfer_id: ${this.transferId}`
          );
          const closeResponse = await closeFile({
            transfer_type: "download",
            download_id: this.transferId,
            random: Math.random(),
          });
          C.log(
            `Download: closeFile response (start download error): ${closeResponse}`
          );

          const { id: fileId } = file;
          store.dispatch("updateFileStatusDownload", {
            fileId,
            updates: {
              status: "canceled",
              downloadingError: "canceled",
            },
          });

          this.nextFileInQueue(true);
        }
      }
    };

    const checkFileAndStartDownload = async () => {
      try {
        C.log(
          `Download: isFileOpen call (start download check) - path: ${file.href_path}`
        );
        const fileStatus = await isFileOpen({
          transfer_type: "download",
          random: Math.random(),
          path: file.href_path,
        });

        if (this.fileClosed) {
          return;
        }

        C.log(`Download: isFileOpen response (start download): ${fileStatus}`);

        if (
          fileStatus !== "CLOSED" &&
          fileStatus.toLowerCase().indexOf("error") < 0 &&
          fileStatus.toLowerCase().indexOf("failure") < 0
        ) {
          setTimeout(() => {
            checkFileAndStartDownload();
          }, 5000);
          return;
        }

        initDownload();
      } catch (error) {
        if (this.fileClosed) {
          return;
        }

        setTimeout(() => {
          checkFileAndStartDownload();
        }, 5000);
      } finally {
        if (this.fileClosed) {
          const { id: fileId } = file;
          store.dispatch("updateFileStatusDownload", {
            fileId,
            updates: {
              status: "canceled",
              downloadingError: "canceled",
            },
          });

          this.nextFileInQueue(true);
        }
      }
    };

    checkFileAndStartDownload();
  };

  closeFileDownloading = async () => {
    const file = this.streamingFile;
    const { id: fileId } = file;
    this.closeFileInProgress = true;
    const that = this;
    try {
      // Close writable stream
      C.success(`File downloaded, finalizing now`);

      const allChunks = chunkNosFromList(this.chunksDownloaded);
      C.log(`Download: All Received Chunks: ${allChunks.join(", ")}`);

      const missingChunks = findRemainingChunks(allChunks);

      if (missingChunks.length > 0) {
        C.error(
          `Download: Missing Chunks: ${missingChunks.join(
            ", "
          )}. Chunks being written: ${Object.keys(that.chunksBeingWritten)
            ?.sort((a, b) => a - b)
            .join(", ")}. Chunks yet to write: ${Object.keys(
            that.chunksRemainsToWrite
          )
            ?.sort((a, b) => a - b)
            .join(", ")}`
        );
      }
      C.log(
        `Download: closeFile call (download completed) - transfer_id: ${this.transferId}`
      );
      const closeResponse = await closeFile({
        transfer_type: "download",
        download_id: this.transferId,
        random: Math.random(),
      });
      C.log(
        `Download: closeFile response (download completed): ${closeResponse}`
      );
      C.time("Copy to disk");
      try {
        await this.writableStream.close();
      } catch (ex) {}
      C.timeEnd("Copy to disk");
      C.success(`File written completely`);

      // Only remove progress panel when both download and write are complete
      const progressDiv = document.getElementById(
        "server-read-progress-download"
      );
      // if (
      //   progressDiv &&
      //   this.bytesWritten >= file.size &&
      //   !this.closeFileInProgress
      // ) {
      //   progressDiv.remove();
      // }

      if (file.storageMethod === "sandbox") {
        store.dispatch("updateFileStatusDownload", {
          fileId,
          updates: {
            status: "sandboxDownloading",
          },
        });

        setTimeout(() => {
          // Set 100% progress when download is truly complete
          store.dispatch("updateFileStatusDownload", {
            fileId,
            updates: {
              status: "downloaded",
              timeTakenToComplete: this.elapsedTime
                ? this.elapsedTime * 1000
                : 200,
              bytesDownloaded: file.size, // Show actual file size (100%)
              fileSize: file.size,
            },
          });
        }, 10000);
      } else {
        // Set 100% progress when download is truly complete
        store.dispatch("updateFileStatusDownload", {
          fileId,
          updates: {
            status: "downloaded",
            timeTakenToComplete: this.elapsedTime
              ? this.elapsedTime * 1000
              : 200,
            bytesDownloaded: file.size, // Show actual file size (100%)
            fileSize: file.size,
          },
        });
      }
      this.downloadEndTime = Date.now();
      clearTimeout(this.compressingTimeoutId);
      this.nextFileInQueue();
    } catch (error) {
      C.log(error);
    } finally {
      store.dispatch("stopAliveSession");
      this.cleanupIntervals();
    }
    this.removeServerReadProgressUI();
    this.markRecoveryProgress("file finalized");
    this.closeFileInProgress = false;
  };

  removeServerReadProgressUI = () => {
    const progressDiv = document.getElementById(
      "server-read-progress-download"
    );
    if (progressDiv) {
      progressDiv.remove();
    }
  };

  setSocketMessageHandler = (socket) => {
    socket.onmessage = (event) =>
      this.handleIncomingSocketMessage(socket, event);
  };

  handleIncomingSocketMessage = (socket, event) => {
    this.lastMessageTime = Date.now();
    if (typeof event.data === "string") {
      C.log(`Download: Received: ${event.data}`);
      this.closeFileWithError(event.data);
      return;
    }

    const buffer = event.data;
    const view = new DataView(buffer);
    const bytesWritten = this.bytesWritten;
    const index = view.getUint32(0);
    const contentLength = view.getUint32(4);
    const chunk = event.data.slice(8);
    this.logChunkEvent(index, "CHUNK_RECEIVED", {
      size: contentLength,
      socketId: socket.debugId,
    });
    this.queueAckChunk(index);

    C.log(
      `Download: Socket#${
        socket.debugId
      } received chunk ${index}, Size: ${parseInt(contentLength)}`
    );

    socket.chunksBeingSent.push(index);
    C.log(`Download: Queued ACK for chunk ${index}`);
    this.lastReceivedChunkIndex = index;

    if (this.chunksDownloaded[index] === undefined) {
      this.chunksDownloaded[index] = index;

      if (this.missingChunksTracker[index]) {
        delete this.missingChunksTracker[index];
        C.log(
          `Download: Chunk ${index} received, removed from missing chunks tracker`
        );
      }
      const chunkSize = parseInt(contentLength);
      this.bytesReceivedForSpeed += chunkSize;
      const bytesDownloaded = (this.bytesDownloaded += chunkSize);

      const streamingFile = this.streamingFile || {};
      const fileId = streamingFile.id;
      const fileSize = streamingFile.size || 0;

      if (fileSize) {
        C.log(
          `Download: ${bytesDownloaded}/${fileSize} (${getPercentage(
            fileSize,
            bytesDownloaded
          )}%)`
        );
      }

      if (parseInt(contentLength) === 0) {
        this.EOFChunkIndex = index;
        C.log(`Download: Received last chunk, 0 size chunk ${index}, ignored`);
      } else {
        this.chunksRemainsToWrite[index] = {
          data: chunk,
          size: chunkSize,
        };
      }

      if (contentLength && fileId) {
        store.dispatch("updateProgressBarDownload", {
          fileId,
          bytesDownloaded,
          bytesWritten,
          fileSize,
        });

        if (this.showServerReadProgress && streamingFile.size) {
          const serverYetToRead =
            (streamingFile.size - bytesDownloaded) / (1024 * 1024);
          C.log(
            `Updating server read progress: ${serverYetToRead.toFixed(
              2
            )} MB remaining`
          );
          this.updateServerReadProgress(serverYetToRead);
        }
      }

      this.checkIfFileDownloaded();
      this.writeChunksToDisk();
    } else {
      C.log(`Download: ${index} exists, ignored`);
      this.writeChunksToDisk();
    }
  };

  markRecoveryProgress = (reason) => {
    if (!this.recoveryInProgress) return;
    const waitTime = this.recoveryWaitStart
      ? Date.now() - this.recoveryWaitStart
      : 0;
    C.log(
      `Download: Recovery completed after ${waitTime}ms (${reason || "n/a"})`
    );
    this.recoveryInProgress = false;
    this.recoveryWaitStart = null;
    this.recoveryAttemptCount = 0;
    this.recoveryPendingChunks = [];
  };

  failRecoveryAttempts = () => {
    if (this.fileClosed) return;
    const pending =
      this.recoveryPendingChunks?.length > 0
        ? ` (chunks: ${this.recoveryPendingChunks.join(", ")})`
        : "";
    C.error(
      `Download: Unable to recover missing chunks after ${this.maxRecoveryAttempts} attempts${pending}`
    );
    this.closeFileWithError(
      "Download failed due to unstable network while recovering missing data."
    );
  };

  delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  handleDiskWriteError = async (message, error) => {
    if (this.diskWriteErrorHandled) {
      return;
    }
    this.diskWriteErrorHandled = true;
    const finalMessage = message || error?.message || "Disk write failed";
    C.error("Download: WS handleDiskWriteError debug", {
      finalMessage,
      messageArg: message,
      errorName: error?.name,
      errorMessage: error?.message,
      errorCauseName: error?.cause?.name,
      errorCauseMessage: error?.cause?.message,
      fileId: this.streamingFile?.id,
      transferId: this.transferId,
      storageMethod: this.streamingFile?.storageMethod,
    });
    C.error(`Download: Disk write error - ${finalMessage}`);
    await this.closeFileWithError(finalMessage);
  };

  // Writes chunks in the disk using the file handle (FileSystemAPI :- FileHandle)
  writeChunksToDisk = async () => {
    writeDownloadedChunksToDisk(this);
  };

  // close all connected sockets, uses when download completes or canceled
  closeAllSockets = () => {
    clearInterval(this.noMsgIntervalId);
    clearInterval(this.elapsedIntervalId);
    clearInterval(this.intervalIdAverageTenSecondSpeed);
    clearInterval(this.chunks_status_interval);
    clearInterval(this.missingChunkCheckInterval);

    // Remove server read progress UI
    // const progressDiv = document.getElementById(
    //   "server-read-progress-download"
    // );
    // if (progressDiv) {
    //   progressDiv.remove();
    // }

    this.sockets.forEach((socket) => {
      C.log(
        `Download: Closing socket#${socket.debugId} due to closeAllSockets (state: ${socket.readyState})`
      );
      socket.close();
    });
  };

  // Cancel downloading file
  cancel = async () => {
    if (this.fileClosed) return;
    this.fileClosed = true;

    if (!this.transferId) {
      return;
    }

    const beforeCancelingFile = () => {
      clearInterval(this.noMsgIntervalId);
      clearInterval(this.elapsedIntervalId);
      clearInterval(this.intervalIdAverageTenSecondSpeed);
      clearInterval(this.chunks_status_interval);
      clearInterval(this.missingChunkCheckInterval);

      this.canceled = true;
      this.fileClosed = true;
      this.closeAllSockets();
      this.removeServerReadProgressUI();
      this.markRecoveryProgress("download canceled");
      this.cleanupIntervals();
    };
    C.log(`Download: CloseFile called as download canceled`);
    const afterCancelingFile = async () => {
      C.log(
        `Download: closeFile call (canceled) - transfer_id: ${this.transferId}`
      );
      const closeResponse = await closeFile({
        transfer_type: "download",
        download_id: this.transferId,
        random: Math.random(),
      });
      C.log(`Download: closeFile response (canceled): ${closeResponse}`);
      this.nextFileInQueue(true);
    };

    cancelDownload({
      streamingFile: this.streamingFile,
      writableStream: this.writableStream,
      beforeCancelingFile,
      afterCancelingFile,
    });
  };

  // Update downloading status globally in the project
  handleUpdateDownloadingStatus = (downloading) => {
    if (
      (downloading === true || downloading === false) &&
      downloading !== this.fileDownloadingInProgress
    ) {
      this.fileDownloadingInProgress = downloading;

      store.dispatch("setChunkProcessingStatus", {
        type: "isDownloading",
        status: this.fileDownloadingInProgress,
      });
    }

    return this.fileDownloadingInProgress;
  };

  closeFileWithError = async (errorMsg) => {
    // Prevent multiple calls to closeFileWithError
    if (this.fileClosed) {
      C.warn("Download: closeFileWithError called but file already closed");
      return;
    }

    const file = this.streamingFile;
    const { id: fileId } = file;
    C.error("Download: closeFileWithError debug", {
      fileId,
      transferId: this.transferId,
      storageMethod: file?.storageMethod,
      errorMsg,
      fallbackTriggered: undefined,
    });

    this.canceled = true;
    this.fileClosed = true;
    this.closeAllSockets();
    this.removeServerReadProgressUI();
    this.markRecoveryProgress(`error: ${errorMsg}`);
    this.cleanupIntervals();

    // Run fallback immediately (before closeFile/cleanup) so it always runs even if closeFile hangs or throws
    const fallbackResult = fallbackToZipDownload(file, errorMsg);
    C.error("Download: closeFileWithError fallback debug", {
      fileId,
      transferId: this.transferId,
      storageMethod: file?.storageMethod,
      errorMsg,
      fallbackTriggered: fallbackResult?.triggered,
      fallbackMessage: fallbackResult?.message,
    });

    try {
      C.log(
        `Download: closeFile call (error) - transfer_id: ${this.transferId}, error: ${errorMsg}`
      );
      const closeResponse = await closeFile({
        transfer_type: "download",
        download_id: this.transferId,
        random: Math.random(),
      });
      C.log(`Download: closeFile response (error): ${closeResponse}`);
    } catch (error) {
      C.error(
        `Download: Error closing file on server (error: ${errorMsg}): ${error}`
      );
    }

    clearInterval(this.elapsedIntervalId);
    clearInterval(this.intervalIdAverageTenSecondSpeed);
    clearInterval(this.chunks_status_interval);

    try {
      // Check if writableStream exists before attempting to close
      if (this.writableStream) {
        // FileSystemWritableFileStream.close() throws if already closed
        // Wrap in try-catch to handle the "Cannot close a closed or closing stream" error
        await this.writableStream.close();
      }
    } catch (error) {
      // Stream may already be closed - this is expected in error scenarios
      if (error.message && error.message.includes("closed")) {
        C.warn(
          `Download: Writable stream already closed, ignoring: ${error.message}`
        );
      } else {
        C.warn(`Download: Error closing writable stream: ${error.message}`);
      }
    }

    try {
      if (file && file.fileHandle) {
        await file.fileHandle.remove();
      }
    } catch (error) {
      C.warn(`Download: Error removing file handle: ${error.message}`);
    }

    store.dispatch("updateFileStatusDownload", {
      fileId,
      updates: {
        status: "failed",
        downloadingError: fallbackResult.triggered
          ? fallbackResult.message
          : "File failed please retry to download file: " + errorMsg,
      },
    });

    this.nextFileInQueue(true);
  };

  cleanupIntervals = () => {
    clearInterval(this.elapsedIntervalId);
    clearInterval(this.intervalIdAverageTenSecondSpeed);
    clearInterval(this.chunks_status_interval);
    clearInterval(this.noMsgIntervalId);
    clearInterval(this.missingChunkCheckInterval);
    this.stopAckFlushInterval({ flush: true });
    this.elapsedIntervalId = null;
    this.intervalIdAverageTenSecondSpeed = null;
    this.chunks_status_interval = null;
    this.noMsgIntervalId = null;
    this.missingChunkCheckInterval = null;
  };

  remove = (fileId) => {
    if (!fileId) return;
    this.queue = this.queue.filter(({ id }) => id !== fileId);
    store.dispatch("deleteFileInDownload", { id: fileId });
  };

  // pauses downloading file
  pause = () => {
    const onPause = () => {
      clearInterval(this.chunks_status_interval);
    };
    pauseDownload(this, onPause);
    this.closeAllSockets();
    this.stopAckFlushInterval({ flush: true });
    pausedDownload(this);
  };

  resume = () => {
    resumeDownload(this, this.startDownloading);
  };

  updateServerReadProgress = (serverYetToRead) => {
    if (this.isPause || this.fileClosed) {
      C.log("Not updating progress - paused or closed");
      return;
    }

    // Throttle updates to once every 500ms
    if (this.lastUpdateTime && Date.now() - this.lastUpdateTime < 500) {
      return;
    }
    this.lastUpdateTime = Date.now();

    const globalDownloads = document.getElementById("globalDownloads");
    if (!globalDownloads) {
      C.log("Global downloads div not found");
      return;
    }

    let progressDiv = document.getElementById("server-read-progress-download");
    if (!progressDiv) return;

    _$(progressDiv).show();

    this.updateRecoveryMessage(progressDiv);
    this.updateProgressBar(progressDiv, serverYetToRead);
    this.updateSocketTable(progressDiv);
  };

  updateRecoveryMessage = (progressDiv) => {
    let recoveryMessage = progressDiv.querySelector(".recovery-message");
    if (!recoveryMessage) {
      recoveryMessage = document.createElement("div");
      recoveryMessage.className = "recovery-message";
      // NOTE: Object.assign with style violates CSP. Using utility classes and _$.css() instead.
      recoveryMessage.className = "recovery-message pos-absolute t-5px r-5px p-2px-5px br-3px fs-12px has-text-weight-bold";
      _$(recoveryMessage).css("backgroundColor", "rgba(255, 255, 0, 0.7)");
      progressDiv.appendChild(recoveryMessage);
    }

    const isRecovering = this.sockets.some((socket) => socket.isStuck);
    recoveryMessage.textContent = isRecovering ? "Recovery in progress..." : "";
    if (isRecovering) {
      _$(recoveryMessage).show();
    } else {
      _$(recoveryMessage).hide();
    }
  };

  updateProgressBar = (progressDiv, serverYetToRead) => {
    const progressText = progressDiv.querySelector(".progress-text");
    const { size: fileSize } = this.streamingFile;

    // Calculate percentages for received and written bytes
    const receivedPercentage = (this.bytesDownloaded / fileSize) * 100;
    const writtenPercentage = (this.bytesWritten / fileSize) * 100;

    // Remove old progress bar if it exists (from template)
    const oldProgressBar = progressDiv.querySelector(".progress-bar:not(.progress-bars-container .progress-bar)");
    if (oldProgressBar) {
      oldProgressBar.remove();
    }

    // Create container for both progress bars - ensure it doesn't already exist
    // Progress bars will overlap to show download vs write speed comparison
    let progressBarsContainer = progressDiv.querySelector(
      ".progress-bars-container"
    );
    if (!progressBarsContainer) {
      progressBarsContainer = document.createElement("div");
      progressBarsContainer.className = "progress-bars-container";
      // Insert before progress text
      if (progressText && progressText.parentNode) {
        progressText.parentNode.insertBefore(progressBarsContainer, progressText);
      } else {
        progressDiv.appendChild(progressBarsContainer);
      }
    }

    // Create or get the download progress bar container (bottom layer)
    let downloadContainer = progressBarsContainer.querySelector(
      ".download-container"
    );
    if (!downloadContainer) {
      downloadContainer = document.createElement("div");
      downloadContainer.className = "download-container";
      progressBarsContainer.appendChild(downloadContainer);
    }

    // Create or get the write progress bar container (top layer, overlapping)
    let writeContainer =
      progressBarsContainer.querySelector(".write-container");
    if (!writeContainer) {
      writeContainer = document.createElement("div");
      writeContainer.className = "write-container";
      progressBarsContainer.appendChild(writeContainer);
    }

    // Create or get the download progress bar
    let downloadBar = downloadContainer.querySelector(
      ".download-progress-fill"
    );
    if (!downloadBar) {
      downloadBar = document.createElement("div");
      downloadBar.className = "download-progress-fill";
      // NOTE: Complex cssText replaced with utility classes for CSP compliance
      downloadBar.className = "download-progress-fill progress-fill-base progress-fill-download";
      downloadContainer.appendChild(downloadBar);
    }
    // Round percentage to nearest integer for utility class compatibility
    const roundedPercentage = Math.round(receivedPercentage);
    _$(downloadBar).css("width", `${roundedPercentage}%`);

    // Create or get the write progress bar
    let writeBar = writeContainer.querySelector(".write-progress-fill");
    if (!writeBar) {
      writeBar = document.createElement("div");
      writeBar.className = "write-progress-fill";
      // NOTE: Complex cssText replaced with utility classes for CSP compliance
      writeBar.className = "write-progress-fill progress-fill-base progress-fill-write";
      writeContainer.appendChild(writeBar);
    }
    // Round percentage to nearest integer for utility class compatibility
    const roundedWrittenPercentage = Math.round(writtenPercentage);
    _$(writeBar).css("width", `${roundedWrittenPercentage}%`);

    // Update text to show both received and written progress
    progressText.textContent = `Downloaded: ${(
      this.bytesDownloaded /
      (1024 * 1024)
    ).toFixed(2)} MB / ${(fileSize / (1024 * 1024)).toFixed(
      2
    )} MB | Written: ${(this.bytesWritten / (1024 * 1024)).toFixed(2)} MB`;
  };

  updateSocketTable = (progressDiv) => {
    let socketTable = progressDiv.querySelector(".socket-info-table");
    if (!socketTable) {
      socketTable = document.createElement("table");
      socketTable.className = "socket-info-table";
      // NOTE: Object.assign with style violates CSP. Using utility classes and _$.css() instead.
      socketTable.className = "socket-info-table w-100 mt-10px";
      _$(socketTable).css("borderCollapse", "collapse");
      progressDiv.appendChild(socketTable);

      // Add copy button only if showServerReadProgress is enabled
      if (this.showServerReadProgress) {
        const copyButton = document.createElement("button");
        copyButton.className = "copy-debug-info";
        copyButton.setAttribute(
          "data-tooltip",
          "Copy debug info (alt+click to download)"
        );
        copyButton.innerHTML = '<i class="icon-paste"></i>';
        // NOTE: Object.assign with style violates CSP. Using utility classes and _$.css() instead.
        copyButton.className = "copy-debug-info pos-absolute b-5px r-5px op-0-7 p-5px-10px border-none br-3px cursor-pointer fs-12px";
        _$(copyButton).css("color", "black");
        copyButton.onclick = (event) => this.copyDebugInfo(event);
        progressDiv.appendChild(copyButton);
      }
    } else {
      // Update copy button visibility if it exists
      const copyButton = progressDiv.querySelector(".copy-debug-info");
      if (copyButton) {
        if (this.showServerReadProgress) {
          _$(copyButton).show();
        } else {
          _$(copyButton).hide();
        }
      }
    }

    let [indexRow, bufferRow] = this.getOrCreateRows(socketTable);
    this.updateSocketRows(indexRow, bufferRow);
    this.updateStatusRow(socketTable);
    this.updateSummaryRow(socketTable);
  };

  getOrCreateRows = (table) => {
    const createRow = (className) => {
      let row = table.querySelector(`.${className}`);
      if (!row) {
        row = table.insertRow();
        row.className = className;
      }
      // Clear existing cells before rebuilding
      row.innerHTML = "";
      return row;
    };
    return [createRow("index-row"), createRow("buffer-row")];
  };

  updateSocketRows = (indexRow, bufferRow) => {
    // Clear rows first (already done in getOrCreateRows, but ensure it's clean)
    indexRow.innerHTML = "";
    bufferRow.innerHTML = "";

    // Create header cells
    const socketHeaderCell = indexRow.insertCell();
    socketHeaderCell.className = "text-primaryMain w-80px text-left";
    socketHeaderCell.textContent = "Socket:";
    
    const chunksHeaderCell = bufferRow.insertCell();
    chunksHeaderCell.className = "text-primaryMain w-80px text-left";
    chunksHeaderCell.textContent = "Chunks:";

    const cellWidth = `${100 / (this.sockets.length + 1)}%`;

    this.sockets.forEach((socket, index) => {
      const chunksInBuffer = socket.chunksBeingSent?.length || 0;
      //if socket is closed, show index in red
      const socketIndexCell = indexRow.insertCell();
      socketIndexCell.className = `text-center`;
      _$(socketIndexCell).css("width", cellWidth);
      const socketIndexStrong = document.createElement("strong");
      socketIndexStrong.textContent = `${index + 1}`;
      if (socket.readyState === WebSocket.CLOSED) {
        socketIndexStrong.className = "text-error";
      }
      socketIndexCell.appendChild(socketIndexStrong);
      
      const chunksCell = bufferRow.insertCell();
      chunksCell.className = "text-center";
      _$(chunksCell).css("width", cellWidth);
      chunksCell.textContent = `${chunksInBuffer}`;
    });

    // Add total column
    const totalChunksInBuffer = this.sockets.reduce(
      (total, socket) => total + (socket.chunksBeingSent?.length || 0),
      0
    );

    const totalIndexCell = indexRow.insertCell();
    totalIndexCell.className = "text-center has-text-weight-bold";
    _$(totalIndexCell).css("width", cellWidth);
    const totalIndexStrong = document.createElement("strong");
    totalIndexStrong.textContent = "T";
    totalIndexCell.appendChild(totalIndexStrong);
    
    const totalChunksCell = bufferRow.insertCell();
    totalChunksCell.className = "text-center has-text-weight-bold";
    _$(totalChunksCell).css("width", cellWidth);
    totalChunksCell.textContent = `${totalChunksInBuffer}`;
  };

  createCell = (row, width, content, isTotal = false) => {
    const cell = row.insertCell();
    // Round width to nearest utility class if it's a pixel value
    const widthValue = width.includes("px") 
      ? `${Math.round(parseFloat(width))}px` 
      : width;
    _$(cell).css("width", widthValue);
    cell.className = `text-center ${isTotal ? "has-text-weight-bold" : ""}`;
    cell.innerHTML = content;
  };

  updateStatusRow = (socketTable) => {
    const chunksReadyToWrite = Object.keys(this.chunksRemainsToWrite).length;
    let statusRow =
      socketTable.querySelector(".status-row") || socketTable.insertRow();
    statusRow.className = "status-row";
    statusRow.innerHTML = `<td colspan="${
      this.sockets.length + 2
    }" class="text-left">Chunks ready to write: ${chunksReadyToWrite}</td>`;
  };

  updateSummaryRow = (socketTable) => {
    if (this.isPause || this.fileClosed) return;

    const summary = {
      totalChunksReceived: Object.keys(this.chunksDownloaded).length,
      chunksWritten: Object.keys(this.chunksWritten).length,
      chunksBeingWritten: Object.keys(this.chunksBeingWritten).length,
      chunksToWrite: Object.keys(this.chunksRemainsToWrite).length,
    };

    let summaryRow =
      socketTable.querySelector(".summary-row") || socketTable.insertRow();
    summaryRow.className = "summary-row";
    summaryRow.innerHTML = `
      <td colspan="${this.sockets.length + 2}" class="text-left">
        Total chunks received: ${summary.totalChunksReceived} |
        Written: ${summary.chunksWritten} |
        Writing: ${summary.chunksBeingWritten} |
        To write: ${summary.chunksToWrite}
      </td>
    `;
  };

  randomlyCloseSocket = () => {
    C.log(
      `Download: Recovery: Test recovery check - testRecovery: ${this.testRecovery}, isPause: ${this.isPause}, fileClosed: ${this.fileClosed}, fileDownloaded: ${this.fileDownloaded}`
    );
    if (
      !this.testRecovery ||
      this.isPause ||
      this.fileClosed ||
      this.fileDownloaded
    ) {
      C.log(
        `Download: Recovery: Skipping random socket close because: ${
          !this.testRecovery
            ? "test recovery disabled"
            : this.isPause
              ? "download is paused"
              : this.fileClosed
                ? "file is closed"
                : "file is downloaded"
        }`
      );
      return;
    }

    setTimeout(() => {
      this.randomlyCloseSocket();
    }, 10000);

    const now = Date.now();
    const timeSinceLastClose = now - this.lastRandomCloseTime;
    C.log(
      `Download: Recovery: Time since last random close: ${
        timeSinceLastClose / 1000
      }s (minimum interval: ${this.randomCloseInterval / 1000}s)`
    );

    if (timeSinceLastClose < this.randomCloseInterval) {
      C.log(
        `Download: Recovery: Skipping random socket close - too soon since last close`
      );
      return;
    }

    const randomValue = Math.random();
    C.log(
      `Download: Recovery: Random value: ${randomValue.toFixed(
        2
      )} (threshold: 0.1)`
    );

    if (randomValue < 0.3) {
      const openSockets = this.sockets.filter(
        (socket) =>
          (socket.readyState === WebSocket.OPEN ||
            socket.readyState === WebSocket.CONNECTING) &&
          socket.chunksBeingSent?.length > 0
      );

      C.log(
        `Download: Found ${openSockets.length} eligible sockets for closing (${this.sockets.length} total sockets)`
      );

      if (openSockets.length > 0) {
        C.warn(
          `Download: Randomly closing ${
            openSockets.length
          } active sockets for testing recovery. Socket indices: ${openSockets
            .map((s) => s.index)
            .join(", ")}`
        );
        this.lastRandomCloseTime = now;
        openSockets.forEach((socket) => {
          C.log(
            `Download: Closing socket ${socket.index} with ${socket.chunksBeingSent?.length} chunks being sent`
          );
          socket.close();
        });
      } else {
        C.log(
          `Download: No eligible sockets found to close (need open sockets with chunks being sent)`
        );
      }
    } else {
      C.log(
        `Download: Random value ${randomValue.toFixed(
          2
        )} did not meet threshold 0.1 - no sockets closed`
      );
    }
  };

  // ========== DEBUG INFO HELPER METHODS ==========

  // Log chunk events for detailed tracking (receive, ack, ack_below, bulk ack)
  logChunkEvent = (chunkIndex, eventType, details = {}) => {
    if (!this.showServerReadProgress) return;

    const event = {
      chunkIndex,
      eventType,
      timestamp: Date.now(),
      details,
    };

    if (!this.downloadSummary.chunkEvents) {
      this.downloadSummary.chunkEvents = [];
    }
    this.downloadSummary.chunkEvents.push(event);

    // Minimal console action logging for key events
    const essentialEvents = [
      "CHUNK_RECEIVED",
      "CHUNK_ACK_QUEUED",
      "CHUNK_ACK_FLUSH",
      "CHUNK_ACK_FLUSH_FAIL",
      "CHUNK_ACK_BELOW",
      "CHUNK_ACK_BELOW_FAIL",
    ];

    if (essentialEvents.includes(eventType)) {
      const minimalContext = {
        chunk: chunkIndex,
      };
      if (details.socketId !== undefined) {
        minimalContext.socket = details.socketId;
      }
      if (details.chunks) {
        minimalContext.chunks = details.chunks;
      }
      if (details.error) {
        minimalContext.error = details.error;
      }
      C.logAction("chunk", eventType.toLowerCase(), minimalContext);
    }
  };

  // Summarize chunk events for debug info output
  formatChunkEventsSummary = () => {
    if (!this.showServerReadProgress) {
      return ["Debug logging disabled (showServerReadProgress is false)"];
    }

    const events = this.downloadSummary.chunkEvents || [];
    const result = [];

    result.push("=== CHUNK EVENTS SUMMARY ===");
    result.push(`Total chunk events: ${events.length}`);

    if (events.length === 0) {
      result.push("No chunk events found");
      return result;
    }

    // Group counts by event type
    const eventCounts = {};
    events.forEach((event) => {
      eventCounts[event.eventType] = (eventCounts[event.eventType] || 0) + 1;
    });
    result.push("");
    result.push("Event counts:");
    Object.entries(eventCounts).forEach(([type, count]) => {
      result.push(`- ${type}: ${count}`);
    });

    // Show all events in chronological order (by timestamp)
    result.push("");
    events
      .sort((a, b) => a.timestamp - b.timestamp)
      .forEach((event) => {
        const time = new Date(event.timestamp).toLocaleTimeString();
        const chunkInfo =
          event.chunkIndex !== null && event.chunkIndex !== undefined
            ? `Chunk ${event.chunkIndex}: `
            : "";
        const extra =
          event.details && Object.keys(event.details).length > 0
            ? ` | ${JSON.stringify(event.details)}`
            : "";
        result.push(`${time} - ${chunkInfo}${event.eventType}${extra}`);
      });

    return result;
  };

  copyDebugInfo = (event) => {
    if (!this.showServerReadProgress) {
      C.warn(
        "Debug information is disabled. Enable showServerReadProgress to view debug info"
      );
      return;
    }

    // Get the current downloading file instance to ensure we always copy the right file's debug info
    const currentDownloadInstance =
      downloadingQueue.getCurrentDownloadingFileInstance();

    // Use the current downloading instance if available, otherwise fall back to this instance
    const downloadInstance = currentDownloadInstance || this;
    const debugInfo = downloadInstance.formatDebugInfo();

    // Check if alt key is pressed
    if (event && event.altKey) {
      // Create filename with date-time
      const now = new Date();
      const dateTime = now
        .toISOString()
        .replace(/[:.]/g, "-")
        .replace("T", "-")
        .slice(0, 19);
      const filename = `file_download_stats-${dateTime}.txt`;

      // Create and download file
      const blob = new Blob([debugInfo], { type: "text/plain" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      C.log("Debug information downloaded as file");
      growl(
        "Debug info downloaded",
        "Debug information saved as a file.",
        2000,
        false
      );
    } else {
      // Original clipboard copy behavior
      navigator.clipboard
        .writeText(debugInfo)
        .then(() => {
          C.log("Debug information copied to clipboard");
          growl(
            "Debug info copied",
            "Debug information copied to clipboard.",
            2000,
            false
          );
        })
        .catch((err) => {
          console.error("Failed to copy debug info:", err);
          C.error("Failed to copy debug information");
          growl(
            "Failed to copy debug info",
            "Unable to copy debug information.",
            2000,
            true
          );
        });
    }
  };

  formatDebugInfo = () => {
    if (!this.showServerReadProgress) {
      return "Debug logging is disabled. Enable showServerReadProgress to view debug information.";
    }

    try {
      const {
        size: fileSize,
        id: fileId,
        name,
        nameF,
        href_path,
      } = this.streamingFile || {};
      const fileName = nameF || name || "Unknown";
      const allChunks = chunkNosFromList(this.chunksDownloaded);
      const writtenChunks = chunkNosFromList(this.chunksWritten);
      const missingChunks = findRemainingChunks(allChunks);

      const debugText = [
        "=== Download Debug Information ===",
        `Time: ${new Date().toLocaleString()}`,
        `File: ${fileName}`,
        `File ID: ${fileId || "Unknown"}`,
        `Path: ${href_path || "Unknown"}`,
        `Size: ${utils.formatBytes(fileSize || 0)}`,
        `Transfer ID: ${this.transferId || "Unknown"}`,
        `Download started: ${
          this.downloadStartTime
            ? new Date(this.downloadStartTime).toLocaleString()
            : "N/A"
        }`,
        `Download ended: ${
          this.downloadEndTime
            ? new Date(this.downloadEndTime).toLocaleString()
            : "In progress"
        }`,
        `Total download time: ${
          this.downloadStartTime && this.downloadEndTime
            ? ((this.downloadEndTime - this.downloadStartTime) / 1000).toFixed(
                2
              ) + "s"
            : "N/A"
        }`,
        "",
        "=== Download Status ===",
        `Status: ${
          this.fileDownloaded
            ? "Downloaded"
            : this.fileClosed
              ? "Closed"
              : this.isPause
                ? "Paused"
                : "Downloading"
        }`,
        `Bytes downloaded: ${utils.formatBytes(this.bytesDownloaded)}`,
        `Bytes written: ${utils.formatBytes(this.bytesWritten)}`,
        `Progress: ${
          fileSize
            ? ((this.bytesDownloaded / fileSize) * 100).toFixed(2) + "%"
            : "N/A"
        }`,
        "",
        "=== Socket Information ===",
        `Total sockets: ${this.sockets.length}`,
        `Open sockets: ${
          this.sockets.filter((s) => s.readyState === WebSocket.OPEN).length
        }`,
        `Max requests: ${this.maxRequest}`,
        ...this.sockets.map((socket, index) => {
          const chunksInBuffer = socket.chunksBeingSent?.length || 0;
          const bufferMB = (
            (socket.bufferedAmount || 0) /
            (1024 * 1024)
          ).toFixed(2);
          return `Socket ${index + 1} (ID: ${
            socket.debugId || "N/A"
          }): State: ${
            socket.readyState === WebSocket.OPEN
              ? "OPEN"
              : socket.readyState === WebSocket.CONNECTING
                ? "CONNECTING"
                : socket.readyState === WebSocket.CLOSING
                  ? "CLOSING"
                  : "CLOSED"
          }, Buffer: ${bufferMB}MB, Chunks in buffer: ${chunksInBuffer}, Created: ${
            socket.createdAt
              ? new Date(socket.createdAt).toLocaleTimeString()
              : "N/A"
          }, Reason: ${socket.creationReason || "N/A"}`;
        }),
        "",
        "=== Chunk Status ===",
        `Total chunks received: ${allChunks.length}`,
        `Chunks written: ${writtenChunks.length}`,
        `Chunks being written: ${Object.keys(this.chunksBeingWritten).length}`,
        `Chunks to write: ${Object.keys(this.chunksRemainsToWrite).length}`,
        `Missing chunks: ${
          missingChunks.length > 0 ? missingChunks.join(", ") : "None"
        }`,
        `EOF chunk index: ${
          this.EOFChunkIndex !== null ? this.EOFChunkIndex : "Not received"
        }`,
        `Last received chunk: ${this.lastReceivedChunkIndex}`,
        `Last written chunk: ${this.lastWrittenChunkIndex}`,
        "",
        "=== Recovery Status ===",
        `Recovery in progress: ${this.recoveryInProgress ? "Yes" : "No"}`,
        `Recovery attempts: ${this.recoveryAttemptCount}/${this.maxRecoveryAttempts}`,
        `Recovery pending chunks: ${
          this.recoveryPendingChunks.length > 0
            ? this.recoveryPendingChunks.join(", ")
            : "None"
        }`,
        `Missing chunks tracker: ${
          Object.keys(this.missingChunksTracker).length > 0
            ? Object.keys(this.missingChunksTracker).join(", ")
            : "None"
        }`,
        "",
        "=== ACK Status ===",
        `Pending ACK chunks: ${
          this.pendingAckChunks ? this.pendingAckChunks.size : 0
        }`,
        `ACK flush in progress: ${this.isAckFlushInProgress ? "Yes" : "No"}`,
        `Last ACK below sent at: ${
          this.lastAckBelowSentAt
            ? new Date(this.lastAckBelowSentAt).toLocaleTimeString()
            : "Never"
        }`,
        `Last ACK below chunk index: ${
          this.lastAckBelowChunkIndex !== null
            ? this.lastAckBelowChunkIndex
            : "N/A"
        }`,
        "",
        "=== Timing Information ===",
        `Elapsed time: ${
          this.elapsedTime ? Math.floor(this.elapsedTime) + "s" : "0s"
        }`,
        `Last message time: ${
          this.lastMessageTime
            ? Math.floor((Date.now() - this.lastMessageTime) / 1000) + "s ago"
            : "N/A"
        }`,
        `Last update time: ${
          this.lastUpdateTime
            ? new Date(this.lastUpdateTime).toLocaleTimeString()
            : "N/A"
        }`,
        "",
        "=== File Status ===",
        `File closed: ${this.fileClosed ? "Yes" : "No"}`,
        `File downloaded: ${this.fileDownloaded ? "Yes" : "No"}`,
        `Paused: ${this.isPause ? "Yes" : "No"}`,
        `Session expired: ${this.sessionExpired ? "Yes" : "No"}`,
        "",
        "=== Speed Information ===",
        `Current speed: ${utils.formatBytes(this.speed || 0)}/s`,
        `Overall speed: ${
          this.elapsedTime > 0
            ? utils.formatBytes(this.bytesDownloaded / this.elapsedTime) + "/s"
            : "N/A"
        }`,
        `Bytes received for speed: ${utils.formatBytes(
          this.bytesReceivedForSpeed || 0
        )}`,
        "",
        ...this.formatChunkEventsSummary(),
        "",
        "=== VERBOSE ACTION LOGS ===",
        "These include the same timestamped console logs for full traceability.",
        C.exportDebugLogs({
          includeDedupeInfo: true,
          filterCategory: null,
          maxLines: 50000,
        }),
        "========================",
        "",
      ].join("\n");

      return debugText;
    } catch (error) {
      return `Error generating debug info: ${error.message}\n`;
    }
  };
}

export default WSAdvanceDownload;
